import { GoogleAnalytics } from './google-analytics/analytics-handler';
import { FacebookPixel } from './facebook-pixel/analytics-handler';
import type { IncomingPayment } from '@tectonics/prs/incoming-payment';
import type { AnalyticsManager, AnalyticsEventType, AnalyticsProvider } from './types';
import type { UserInputValues } from '@tectonics/prs/user-input-values';
import type { PlaceOrderErrorPayload } from '@tectonics/prs/outgoing-payment';
import type { Maybe } from '@tectonics/utils/types';

interface InitAnalyticsArgs {
  readonly incomingPayment: IncomingPayment;
}

export function initAnalytics({ incomingPayment }: InitAnalyticsArgs): AnalyticsManager {
  const providers: AnalyticsProvider[] = [new GoogleAnalytics(), new FacebookPixel()];

  const initializedProviders = providers.filter(provider => {
    try {
      return provider.init(incomingPayment);
    } catch (e) {
      // A failing analytics provider must not break checkout; it is skipped and the others keep running.
      // eslint-disable-next-line no-console
      console.warn('Analytics provider initialization error:', e);
      return false;
    }
  });

  const setUserInputValues = (userInputValues: UserInputValues): void => {
    initializedProviders.forEach(provider => provider.setUserInputValues(userInputValues));
  };

  const setSubmitError = (submitError: Maybe<PlaceOrderErrorPayload>): void => {
    initializedProviders.forEach(provider => provider.setSubmitError(submitError));
  };

  const event = (eventType: AnalyticsEventType): void => {
    initializedProviders.forEach(provider => provider.event(eventType));
  };

  const eventOnce = (eventType: AnalyticsEventType, incomingPayment: IncomingPayment): void => {
    initializedProviders.forEach(provider => provider.eventOnce(eventType, incomingPayment));
  };

  return { setUserInputValues, setSubmitError, event, eventOnce };
}
